/**
 * Раздел «Банк» (этап 3, Б2): схемы запросов и DTO. Паспорт —
 * docs/features/client-payments.md.
 */
import { z } from 'zod'
import {
  BANK_LINE_MANUAL_STATUSES,
  type BankLineKind,
  type BankLineStatus,
  type BankMatchState,
  type BankSkipReason,
  type OrderPaymentSource,
} from '../constants/bank'
import { SellerEntity } from '../constants/orders'
import type { BankInvoiceRef } from '../utils/bank-statement'

/** Выписка за месяц по активному счёту — сотни килобайт; с запасом. */
export const BANK_STATEMENT_MAX_CHARS = 4_000_000

/** Загрузка выписки: сайт перекодирует файл из Windows-1251 и присылает текст. */
export const UploadBankStatementSchema = z.object({
  fileName: z.string().trim().min(1).max(300),
  content: z.string().min(1).max(BANK_STATEMENT_MAX_CHARS),
  /** Юрлицо расчётного счёта — спрашиваем при первой выписке по счёту без юрлица. */
  entityId: z.nativeEnum(SellerEntity).optional(),
})
export type UploadBankStatementInput = z.infer<typeof UploadBankStatementSchema>

export const UpdateBankAccountSchema = z.object({
  entityId: z.nativeEnum(SellerEntity).nullable(),
})
export type UpdateBankAccountInput = z.infer<typeof UpdateBankAccountSchema>

export const UpdateBankLineSchema = z.object({
  status: z.enum(BANK_LINE_MANUAL_STATUSES),
})
export type UpdateBankLineInput = z.infer<typeof UpdateBankLineSchema>

export interface BankAccountDto {
  id: string
  accountNumber: string
  entityId: SellerEntity | null
  ownerName: string | null
  ownerInn: string | null
  bankName: string | null
  bik: string | null
}

export interface BankStatementDto {
  id: string
  account: BankAccountDto
  fileName: string
  periodFrom: string | null
  periodTo: string | null
  openingBalance: number | null
  closingBalance: number | null
  totalIn: number | null
  totalOut: number | null
  /** Строк в файле / из них новых (остальные уже были загружены раньше). */
  linesTotal: number
  linesNew: number
  /** Строк этой выписки, которые ждут разноса. */
  pendingCount: number
  uploadedAt: string
  uploadedByName: string | null
}

export interface BankLineDto {
  id: string
  statementId: string
  docType: string
  docNumber: string
  docDate: string | null
  postedAt: string | null
  direction: 'IN' | 'OUT'
  amount: number
  /** Контрагент: плательщик у поступления, получатель у списания. */
  counterpartyName: string
  counterpartyInn: string
  purpose: string
  kind: BankLineKind
  status: BankLineStatus
  /** Почему программа сама поставила «не учитываем»; у остальных — null. */
  skipReason: BankSkipReason | null
  invoiceRefs: BankInvoiceRef[]
  /** Сколько уже разнесено по заказам и сколько осталось. */
  allocatedAmount: number
  remaining: number
  /** Клиент, которому принадлежит поступление (после разноса или выбора). */
  client: { id: string; name: string } | null
  /** Куда разнесено. */
  allocations: BankLineAllocationDto[]
  /** Что программа предлагает для открытой строки; у закрытых — null. */
  match: BankLineMatchDto | null
  /** Период и счёт выписки — для общей очереди «не разнесено». */
  statementPeriod: string | null
}

export interface BankLineAllocationDto {
  paymentId: string
  orderId: string
  requestNumber: string | null
  invoiceNumber: string | null
  amount: number
  /** MANUAL — ручная оплата, привязанная к строке вместо создания новой. */
  source: OrderPaymentSource
}

export interface BankLineMatchDto {
  state: BankMatchState
  /** Почему так — одной фразой по-русски. */
  reason: string
  clientName: string | null
  /** Короткая подпись предложения: «Заказ 2609-015, счёт 1298». */
  proposalLabel: string | null
}

/** Одна часть разноса: сколько из строки идёт в какой заказ. */
export const BankAllocationItemSchema = z.object({
  orderId: z.string().uuid(),
  invoiceId: z.string().uuid().nullable().optional(),
  // До копеек: 0,004 ₽ проходил проверку и записывался оплатой 0,00 (аудит 20.10.2026).
  amount: z
    .number()
    .finite()
    .max(1_000_000_000)
    .transform((v) => Math.round(v * 100) / 100)
    .refine((v) => v > 0, 'Сумма разноса — от 1 копейки'),
  /** Ручная оплата заказа на ту же сумму: привязать строку к ней, новую не создавать. */
  linkPaymentId: z.string().uuid().nullable().optional(),
})
export type BankAllocationItem = z.infer<typeof BankAllocationItemSchema>

export const AllocateBankLineSchema = z.object({
  clientId: z.string().uuid(),
  items: z.array(BankAllocationItemSchema).min(1).max(50),
  /** Записать ИНН плательщика клиенту, у которого ИНН не заполнен. */
  saveInn: z.boolean().optional(),
  /** Записать номер счёта из назначения платежа в счёт заказа без номера. */
  invoiceNumbers: z
    .array(z.object({ invoiceId: z.string().uuid(), number: z.string().trim().min(1).max(40) }))
    .max(50)
    .optional(),
})
export type AllocateBankLineInput = z.infer<typeof AllocateBankLineSchema>

export interface BankCandidateInvoiceDto {
  id: string
  invoiceNumber: string | null
  issuedAt: string | null
  entityId: string | null
  totalSum: number
  paid: number
  remaining: number
}

export interface BankCandidateOrderDto {
  orderId: string
  requestNumber: string | null
  status: string
  managerName: string | null
  /** Сумма выставленных счетов (если счетов нет — сумма заказа). */
  totalSum: number
  paid: number
  remaining: number
  invoices: BankCandidateInvoiceDto[]
  /** Ручные оплаты заказа, ещё не подтверждённые банком. */
  manualPayments: Array<{ id: string; amount: number; paidAt: string | null }>
}

export interface BankCandidateClientDto {
  id: string
  name: string
  inn: string | null
  managerName: string | null
}

/** Всё, что нужно окну разноса одной строки. */
export interface BankLineCandidatesDto {
  line: BankLineDto
  /** Юрлицо расчётного счёта выписки (null — не указано, по номеру счёта не ищем). */
  entityId: string | null
  /** Клиенты с ИНН плательщика (или выбранный вручную). */
  clients: BankCandidateClientDto[]
  clientId: string | null
  orders: BankCandidateOrderDto[]
  state: BankMatchState
  reason: string
  proposal: BankAllocationItem[]
  /** Номер счёта из назначения, который можно записать в счёт заказа без номера. */
  invoiceNumberHint: { invoiceId: string; number: string } | null
}

export interface BankAutoAllocateResultDto {
  /** Сколько строк разнесено автоматически. */
  allocated: number
  /** Сколько открытых строк осталось на ручной разбор. */
  left: number
}

export interface BankStatementDetailDto extends BankStatementDto {
  lines: BankLineDto[]
}

// ── Реестр счетов 1С (Б4): номера счетов из 1С в счета заказов ──

/** Строка реестра счетов из журнала документов 1С (файл разбирает сайт). */
export const InvoiceRegistryRowSchema = z.object({
  /** Номер счёта, как в 1С: «сэНФ-001298». */
  number: z.string().trim().min(1).max(40),
  /** Дата счёта `YYYY-MM-DD`. */
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable(),
  /** Код покупателя в 1С — тот же, что «Код» клиента в микрохабе. */
  clientCode: z.string().trim().max(64),
  buyer: z.string().trim().max(300),
  sum: z.number().finite().nonnegative().max(1_000_000_000_000).nullable(),
  state: z.string().trim().max(100).nullable(),
})
export type InvoiceRegistryRow = z.infer<typeof InvoiceRegistryRowSchema>

export const UploadInvoiceRegistrySchema = z.object({
  rows: z.array(InvoiceRegistryRowSchema).min(1).max(5000),
})
export type UploadInvoiceRegistryInput = z.infer<typeof UploadInvoiceRegistrySchema>

export const AssignInvoiceNumberSchema = z.object({
  invoiceId: z.string().uuid(),
  number: z.string().trim().min(1).max(40),
})
export type AssignInvoiceNumberInput = z.infer<typeof AssignInvoiceNumberSchema>

export interface InvoiceRegistryCandidateDto {
  invoiceId: string
  orderId: string
  requestNumber: string | null
  totalSum: number
  issuedAt: string | null
  managerName: string | null
}

/**
 * Что вышло по счёту реестра:
 *  APPLIED — номер записан в счёт заказа; ALREADY — номер уже стоял;
 *  CHOOSE — у клиента несколько счетов без номера на эту сумму, нужен выбор;
 *  SUM_MISMATCH — счета без номера у клиента есть, но на другие суммы (можно
 *    записать вручную или пропустить);
 *  NO_INVOICES — покупатель найден, счетов без номера у его заказов нет;
 *  NO_CLIENT — покупателя с таким кодом в микрохабе нет;
 *  UNKNOWN_ENTITY — незнакомая приставка номера, юрлицо не определить.
 */
export const INVOICE_REGISTRY_OUTCOMES = [
  'APPLIED',
  'ALREADY',
  'CHOOSE',
  'SUM_MISMATCH',
  'NO_INVOICES',
  'NO_CLIENT',
  'UNKNOWN_ENTITY',
] as const
export type InvoiceRegistryOutcome = (typeof INVOICE_REGISTRY_OUTCOMES)[number]

export const INVOICE_REGISTRY_OUTCOME_LABELS_RU: Record<InvoiceRegistryOutcome, string> = {
  APPLIED: 'Номер записан',
  ALREADY: 'Номер уже стоял',
  CHOOSE: 'Нужен ваш выбор',
  SUM_MISMATCH: 'Сумма не совпала',
  NO_INVOICES: 'Заказа в микрохабе нет',
  NO_CLIENT: 'Покупателя с таким кодом нет',
  UNKNOWN_ENTITY: 'Незнакомая приставка номера',
}

/** Итог по одному счёту реестра. */
export interface InvoiceRegistryRowResultDto {
  number: string
  date: string | null
  buyer: string
  /** Клиент микрохаба, найденный по коду 1С. */
  clientName: string | null
  sum: number | null
  state: string | null
  outcome: InvoiceRegistryOutcome
  reason: string
  /** Счёт заказа, в котором стоит этот номер (APPLIED / ALREADY). */
  target: InvoiceRegistryCandidateDto | null
  /** Счета заказов клиента без номера — для выбора (CHOOSE / SUM_MISMATCH). */
  candidates: InvoiceRegistryCandidateDto[]
}

export interface InvoiceRegistryResultDto {
  rows: InvoiceRegistryRowResultDto[]
  /** Сколько поступлений из очереди разнеслось после записи номеров. */
  autoAllocated: number
}

/**
 * Настройки раздела «Банк» (14.10.2026). Дата начала учёта: оплаты по счетам до неё
 * программа сама помечает «не учитываем» — прошлый период сверяется в 1С.
 */
export const UpdateBankSettingsSchema = z.object({
  accountingStartDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Дата начала учёта — в формате ГГГГ-ММ-ДД')
    .refine((d) => !Number.isNaN(Date.parse(`${d}T00:00:00Z`)), 'Неверная дата начала учёта')
    .nullable(),
})
export type UpdateBankSettingsInput = z.infer<typeof UpdateBankSettingsSchema>

export interface BankSettingsDto {
  /** `YYYY-MM-DD`; null — ограничения нет, разносятся все поступления. */
  accountingStartDate: string | null
  updatedAt: string | null
  updatedByName: string | null
}

export interface UpdateBankSettingsResultDto {
  settings: BankSettingsDto
  /** Сколько неразнесённых строк ушло в «не учитываем» по новой дате. */
  skipped: number
  /** Сколько строк вернулось в работу (дату начала сдвинули раньше или сняли). */
  reopened: number
}
